/**
 * Analytics event schema.
 *
 * Add new events here first, then expose them through a named helper in
 * `product.ts`. Feature code should call product helpers rather than importing
 * this catalog or the Amplitude client directly.
 */
export const AnalyticsEvents = {
  PptTaskpaneSessionStarted: "PPT Taskpane Session Started",
  ExcelTaskpaneSessionStarted: "Excel Taskpane Session Started",
  PptQuerySubmitted: "Query Submitted (PPT)",
  ExcelQuerySubmitted: "Query Submitted (Excel)",
  PptQueryRun: "Query Run (PPT)",
  ExcelQueryRun: "Query Run (Excel)",
  DeckCreationStarted: "Deck Creation Started",
  DeckCreationCompleted: "Deck Creation Completed",
  DeckReviewCompleted: "Deck Review Completed",
  FeedbackSubmitted: "Feedback Submitted",
  FeedbackRatingUpdated: "Feedback Rating Updated",
  FeedbackDetailsSubmitted: "Feedback Details Submitted",
} as const;

export type AnalyticsEventName = (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents];

export type AnalyticsAppSource = "finster_office_addin";
export type AnalyticsHost = "powerpoint" | "excel";
export type AnalyticsSurface = "task_pane" | "dialog";
export type DeckCreationStatus = "complete" | "error" | "cancelled";
export type DeckReviewStatus = "complete" | "error" | "cancelled";
export type QueryRunStatus = "completed" | "failed" | "cancelled";
export type AnalyticsAiMode = "chat" | "create" | "review" | string;
export type FeedbackRatingAction = "submitted" | "changed" | "removed";
export type FeedbackRatingValue = "positive" | "negative";
export type SkillAnalyticsSelectionPath = "explicit" | "inferred" | "none";
export type SkillAnalyticsResolutionStatus =
  | "cleared"
  | "inherited"
  | "resolved"
  | "none"
  | "fail-open"
  | "blocked"
  | "aborted";
export type SkillAnalyticsOrigin = "personal" | "organization" | "finster";

export interface AnalyticsBaseProperties {
  appSource: AnalyticsAppSource;
  host: AnalyticsHost;
  surface: AnalyticsSurface;
  platform: string;
}

export interface TaskpaneSessionStartedProperties extends AnalyticsBaseProperties {}

/**
 * Raised when the user submits a query, before any work happens. Pairs with
 * `QueryRunProperties`, which is only knowable once the run settles, so the two
 * counts together measure how many submitted queries never reported an outcome.
 */
export interface QuerySubmittedProperties extends AnalyticsBaseProperties {
  mode: AnalyticsAiMode;
  hasContext: boolean;
}

export interface QueryRunProperties extends AnalyticsBaseProperties {
  mode: AnalyticsAiMode;
  hasContext: boolean;
  durationMs: number;
  status: QueryRunStatus;
  errorType?: string;
  needsClarification?: boolean;
  workbookEditAttempted?: boolean;
  workbookEditSuccess?: boolean;
  operationCount?: number;
  affectedSheetCount?: number;
}

/**
 * Privacy-safe Skill outcome fields for Langfuse observations. These are not
 * sent to Amplitude and intentionally contain no raw guidance, names, slugs,
 * file names, target metadata, or evidence bodies.
 */
export interface QuerySkillsAnalyticsProperties {
  skillSelectionPath: SkillAnalyticsSelectionPath;
  skillResolutionStatus: SkillAnalyticsResolutionStatus;
  skillCount: number;
  skillOriginSummary?: string;
  skillSelectorPath?: string;
  skillSelectorSource?: string;
  skillPolicyVersion?: number;
  skillResolveLatencyMs?: number;
  skillSelectorLatencyMs?: number;
  skillSelectorFallback?: boolean;
  skillTargetsStripped?: boolean;
  skillEffectiveSourceCount?: number;
  skillEffectiveFileCount?: number;
  skillGuidanceChars?: number;
  skillPlanningEvidenceCount?: number;
  skillPlanningEvidenceCitationCount?: number;
  skillPlanningEvidenceChars?: number;
  skillPlanningEvidenceSizeBytes?: number;
  skillPlanningEvidenceTruncated?: boolean;
}

export interface DeckCreationStartedProperties extends AnalyticsBaseProperties {
  requestedCount: number;
  brandStyle: string | null;
  hasReferenceDeck: boolean;
  taskOutputUsed: boolean;
}

export interface DeckCreationCompletedProperties extends AnalyticsBaseProperties {
  requestedCount: number;
  finalCount: number;
  successCount: number;
  durationMs: number;
  status: DeckCreationStatus;
  brandStyle: string | null;
  taskOutputUsed: boolean;
}

export interface DeckReviewCompletedProperties extends AnalyticsBaseProperties {
  requestedCount: number;
  durationMs: number;
  status: DeckReviewStatus;
  /** Only known once the review runs to completion. */
  slidesReviewed?: number;
  issuesFound?: number;
  errorType?: string;
}

export interface FeedbackSubmittedProperties extends AnalyticsBaseProperties {
  feedbackType: string;
  source: string;
  success: boolean;
}

export interface FeedbackRatingUpdatedProperties extends AnalyticsBaseProperties {
  action: FeedbackRatingAction;
  value: FeedbackRatingValue;
  outputId: string;
  outputType: string;
  area: string;
  sessionId?: string;
  queryId?: string;
  artifactKind?: string;
}

export interface FeedbackDetailsSubmittedProperties extends AnalyticsBaseProperties {
  value: FeedbackRatingValue;
  outputId: string;
  outputType: string;
  area: string;
  sessionId?: string;
  queryId?: string;
  artifactKind?: string;
  reasons?: string[];
}

export type AnalyticsEventProperties = {
  [AnalyticsEvents.PptTaskpaneSessionStarted]: TaskpaneSessionStartedProperties;
  [AnalyticsEvents.ExcelTaskpaneSessionStarted]: TaskpaneSessionStartedProperties;
  [AnalyticsEvents.PptQuerySubmitted]: QuerySubmittedProperties;
  [AnalyticsEvents.ExcelQuerySubmitted]: QuerySubmittedProperties;
  [AnalyticsEvents.PptQueryRun]: QueryRunProperties;
  [AnalyticsEvents.ExcelQueryRun]: QueryRunProperties;
  [AnalyticsEvents.DeckCreationStarted]: DeckCreationStartedProperties;
  [AnalyticsEvents.DeckCreationCompleted]: DeckCreationCompletedProperties;
  [AnalyticsEvents.DeckReviewCompleted]: DeckReviewCompletedProperties;
  [AnalyticsEvents.FeedbackSubmitted]: FeedbackSubmittedProperties;
  [AnalyticsEvents.FeedbackRatingUpdated]: FeedbackRatingUpdatedProperties;
  [AnalyticsEvents.FeedbackDetailsSubmitted]: FeedbackDetailsSubmittedProperties;
};

type AnalyticsPropertyName<E extends AnalyticsEventName> = Extract<
  keyof AnalyticsEventProperties[E],
  string
>;

interface AnalyticsEventDefinition<E extends AnalyticsEventName> {
  description: string;
  requiredProperties: readonly AnalyticsPropertyName<E>[];
}

const BASE_REQUIRED_PROPERTIES = ["appSource", "host", "surface", "platform"] as const;
const QUERY_SUBMITTED_REQUIRED_PROPERTIES = [
  ...BASE_REQUIRED_PROPERTIES,
  "mode",
  "hasContext",
] as const;
const QUERY_RUN_REQUIRED_PROPERTIES = [
  ...QUERY_SUBMITTED_REQUIRED_PROPERTIES,
  "durationMs",
  "status",
] as const;

export const AnalyticsEventCatalog = {
  [AnalyticsEvents.PptTaskpaneSessionStarted]: {
    description: "PowerPoint task pane starts a new de-duplicated analytics session.",
    requiredProperties: [...BASE_REQUIRED_PROPERTIES],
  },
  [AnalyticsEvents.ExcelTaskpaneSessionStarted]: {
    description: "Excel task pane starts a new de-duplicated analytics session.",
    requiredProperties: [...BASE_REQUIRED_PROPERTIES],
  },
  [AnalyticsEvents.PptQuerySubmitted]: {
    description: "User submits a PowerPoint add-in query.",
    requiredProperties: [...QUERY_SUBMITTED_REQUIRED_PROPERTIES],
  },
  [AnalyticsEvents.ExcelQuerySubmitted]: {
    description: "User submits an Excel add-in query.",
    requiredProperties: [...QUERY_SUBMITTED_REQUIRED_PROPERTIES],
  },
  [AnalyticsEvents.PptQueryRun]: {
    description: "PowerPoint add-in query run completes, fails, or is cancelled.",
    requiredProperties: [...QUERY_RUN_REQUIRED_PROPERTIES],
  },
  [AnalyticsEvents.ExcelQueryRun]: {
    description: "Excel add-in query run completes, fails, or is cancelled.",
    requiredProperties: [...QUERY_RUN_REQUIRED_PROPERTIES],
  },
  [AnalyticsEvents.DeckCreationStarted]: {
    description: "User starts PowerPoint deck creation.",
    requiredProperties: [
      ...BASE_REQUIRED_PROPERTIES,
      "requestedCount",
      "brandStyle",
      "hasReferenceDeck",
      "taskOutputUsed",
    ],
  },
  [AnalyticsEvents.DeckCreationCompleted]: {
    description: "PowerPoint deck creation completes, fails, or is cancelled.",
    requiredProperties: [
      ...BASE_REQUIRED_PROPERTIES,
      "requestedCount",
      "finalCount",
      "successCount",
      "durationMs",
      "status",
      "brandStyle",
      "taskOutputUsed",
    ],
  },
  [AnalyticsEvents.DeckReviewCompleted]: {
    description: "PowerPoint deck QA review completes, fails, or is cancelled.",
    requiredProperties: [...BASE_REQUIRED_PROPERTIES, "requestedCount", "durationMs", "status"],
  },
  [AnalyticsEvents.FeedbackSubmitted]: {
    description: "User submits a support or feedback report.",
    requiredProperties: [...BASE_REQUIRED_PROPERTIES, "feedbackType", "source", "success"],
  },
  [AnalyticsEvents.FeedbackRatingUpdated]: {
    description: "User submits, changes, or removes a thumbs-up/down rating on a generated output.",
    requiredProperties: [
      ...BASE_REQUIRED_PROPERTIES,
      "action",
      "value",
      "outputId",
      "outputType",
      "area",
    ],
  },
  [AnalyticsEvents.FeedbackDetailsSubmitted]: {
    description: "User submits optional reason pills for a thumbs-up/down rating.",
    requiredProperties: [...BASE_REQUIRED_PROPERTIES, "value", "outputId", "outputType", "area"],
  },
} satisfies {
  [E in AnalyticsEventName]: AnalyticsEventDefinition<E>;
};
